import {
  Building2,
  CalendarDays,
  ChevronDown,
  CircleDot,
  CircleUserRound,
  Layers,
  Plus,
  Settings2,
  Users
} from "lucide-react";
import {
  type FC,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState
} from "react";
import { useNavigate } from "react-router-dom";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { useCompanies } from "../../companies/queries";
import type { Company } from "../../companies/types";
import { AssignedUsersAvatarStack } from "../../foreignNationals/components/AssignedUsersAvatarStack";
import { caseWorkspacePath } from "../routes";
import { formatDeadline, deadlineUrgency } from "./caseHeaderModel";
import { AssigneesMenu } from "./chips/AssigneesChip";
import { RowLabel, RowValueButton } from "./chips/chip";
import { DeadlinePopover } from "./chips/DeadlineChip";
import { DeadlinesSection } from "./DeadlinesSection";
import { StatusDot, StatusMenu } from "./chips/StatusChip";
import { ImmigrationRows } from "./ImmigrationRows";
import { TimeRow } from "./TimeRow";
import type { CaseDetails } from "./useCaseDetails";

const Row: FC<{ children: ReactNode }> = ({ children }) => (
  <div className="flex h-8 items-center gap-1">{children}</div>
);

const Placeholder: FC<{ children: ReactNode }> = ({ children }) => (
  <span className="text-muted-foreground italic">{children}</span>
);

/** The firm's companies a typed petitioner name picks out, best first. */
export const companyOptions = (
  companies: Company[],
  typed: string,
  limit = 5
): Company[] => {
  const query = typed.trim().toLowerCase();
  if (!query) return companies.slice(0, limit);
  const names = (c: Company) => [
    c.name,
    ...c.entities.map((e) => e.legal_name)
  ];
  return companies
    .filter((c) => names(c).some((n) => n.toLowerCase().includes(query)))
    .sort(
      (a, b) =>
        Number(!names(a).some((n) => n.toLowerCase().startsWith(query))) -
        Number(!names(b).some((n) => n.toLowerCase().startsWith(query)))
    )
    .slice(0, limit);
};

const PetitionerRow: FC<{
  value: string;
  onSubmit: (value: string) => Promise<void>;
  onLink: (company: Company) => Promise<void>;
  onCreate: (name: string) => Promise<void>;
}> = ({ value, onSubmit, onLink, onCreate }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const companies = useCompanies();
  const options = editing ? companyOptions(companies.data ?? [], draft) : [];
  const typed = draft.trim();
  const exact = options.some(
    (c) => c.name.trim().toLowerCase() === typed.toLowerCase()
  );

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [editing, value]);
  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const run = async (write: () => Promise<void>) => {
    setSaving(true);
    try {
      await write();
      setEditing(false);
    } catch {
      // toast raised by the hook; stay in edit mode
    } finally {
      setSaving(false);
    }
  };

  const commit = async () => {
    const next = draft.trim();
    if (next === value.trim()) {
      setEditing(false);
      return;
    }
    await run(() => onSubmit(next));
  };

  // A pick keeps focus in the field, so its blur doesn't save the typed text.
  const keepFocus = (e: MouseEvent) => e.preventDefault();

  return (
    <div className="flex flex-col">
      <Row>
        <RowLabel>
          <Building2 className="size-3.5 text-fg-3" /> Petitioner
        </RowLabel>
        {editing ? (
          <Input
            ref={inputRef}
            autoFocus
            value={draft}
            disabled={saving}
            placeholder="Company or legal business name"
            aria-label="Petitioner"
            className="h-7 flex-1 text-xs"
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => void commit()}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void commit();
              } else if (e.key === "Escape") {
                e.preventDefault();
                setDraft(value);
                setEditing(false);
              }
            }}
          />
        ) : (
          <RowValueButton
            onClick={() => setEditing(true)}
            title={value || undefined}
          >
            {value ? (
              <span className="truncate">{value}</span>
            ) : (
              <Placeholder>Add petitioner</Placeholder>
            )}
          </RowValueButton>
        )}
      </Row>
      {editing && (options.length || typed) ? (
        <div
          className="mb-1 ml-27 flex flex-col divide-y divide-line-1 overflow-hidden rounded-xl ring-1 ring-line-2"
          data-testid="petitioner-options"
        >
          {options.map((company) => (
            <Button
              key={company.id}
              variant="ghost"
              size="xs"
              className="justify-start rounded-none"
              disabled={saving}
              onMouseDown={keepFocus}
              onClick={() => void run(() => onLink(company))}
            >
              <Building2 className="size-3 text-fg-3" />
              <span className="truncate">{company.name}</span>
            </Button>
          ))}
          {typed && !exact ? (
            <Button
              variant="ghost"
              size="xs"
              className="justify-start rounded-none"
              disabled={saving}
              onMouseDown={keepFocus}
              onClick={() => void run(() => onCreate(typed))}
            >
              <Plus className="size-3 text-fg-3" />
              <span className="truncate">Create Company “{typed}”</span>
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

/** Where the details menu's Case Settings action opens this case. */
export const caseSettingsPath = (
  details: Pick<CaseDetails, "individualId" | "foreignNationalId">
): string =>
  caseWorkspacePath(
    {
      individualId: details.individualId,
      foreignNationalId: details.foreignNationalId
    },
    { kind: "settings", id: details.individualId }
  );

/** Where the details menu's Client Profile action opens the client. */
export const clientProfilePath = (foreignNationalId: string): string =>
  `/clients/${foreignNationalId}?tab=overview`;

/** Popover body listing every editable case property. */
export const CaseDetailsBody: FC<{
  details: CaseDetails;
  /** Called when a row moves the attorney elsewhere, so the popover can close. */
  onDone?: () => void;
}> = ({ details, onDone }) => {
  const navigate = useNavigate();
  const urgency = deadlineUrgency(details.filingDeadline);
  const deadlineLabel = formatDeadline(details.filingDeadline);
  const assignedNames = details.teamMembers
    .filter((m) => details.assignedUserIds.includes(m.uid))
    .map((m) => m.label);
  const clientId = details.foreignNationalId;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2.5 pb-2">
        <Avatar size="lg" className="rounded-lg after:rounded-lg">
          {details.clientAvatarUrl ? (
            <AvatarImage
              src={details.clientAvatarUrl}
              alt=""
              className="rounded-lg"
            />
          ) : null}
          <AvatarFallback className="rounded-lg">
            {details.clientInitials}
          </AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-semibold">
            {details.clientName || "Unknown client"}
          </span>
          <span className="truncate text-xs text-muted-foreground">
            {details.workflow || "No workflow"}
          </span>
        </div>
      </div>

      <div className="h-px bg-border" />

      <Row>
        <RowLabel>
          <CircleDot className="size-3.5 text-fg-3" /> Status
        </RowLabel>
        <StatusMenu
          status={details.status}
          onSelect={(s) => void details.setStatus(s)}
          align="start"
        >
          <RowValueButton>
            <StatusDot status={details.status} />
            <span className="flex-1 truncate">{details.status}</span>
            <ChevronDown className="size-3 opacity-0 transition-opacity group-hover/row:opacity-60" />
          </RowValueButton>
        </StatusMenu>
      </Row>

      <Row>
        <RowLabel>
          <CalendarDays className="size-3.5 text-fg-3" /> Filing Deadline
        </RowLabel>
        <DeadlinePopover
          value={details.filingDeadline}
          onChange={details.setFilingDeadline}
          align="start"
        >
          <PopoverTrigger asChild>
            <RowValueButton>
              {deadlineLabel ? (
                <span
                  className={cn(
                    "truncate",
                    urgency === "overdue" && "text-danger",
                    urgency === "soon" && "text-warn"
                  )}
                >
                  {deadlineLabel}
                </span>
              ) : (
                <Placeholder>Add date</Placeholder>
              )}
            </RowValueButton>
          </PopoverTrigger>
        </DeadlinePopover>
      </Row>

      <PetitionerRow
        value={details.petitioner}
        onSubmit={details.setPetitioner}
        onLink={details.linkPetitioner}
        onCreate={details.createPetitioner}
      />

      <Row>
        <RowLabel>
          <Layers className="size-3.5 text-fg-3" /> Workflow
        </RowLabel>
        <span className="flex h-7 min-w-0 flex-1 items-center px-1.5 text-xs">
          {details.workflow ? (
            <span className="truncate">{details.workflow}</span>
          ) : (
            <Placeholder>None</Placeholder>
          )}
        </span>
      </Row>

      <Row>
        <RowLabel>
          <Users className="size-3.5 text-fg-3" /> Assignees
        </RowLabel>
        <AssigneesMenu
          assigned={details.assignedUserIds}
          team={details.teamMembers}
          isLoading={details.isLoadingTeamMembers}
          isSaving={details.isSavingAssignees}
          onChange={details.setAssignees}
          align="start"
        >
          <DropdownMenuTrigger asChild>
            <RowValueButton
              disabled={details.isSavingAssignees}
              // Names are intentionally not rendered; the avatar stack carries
              // them visually and this title exposes them on hover / to AT.
              title={
                assignedNames.length > 0 ? assignedNames.join(", ") : undefined
              }
            >
              {details.assignedUserIds.length > 0 ? (
                <span className="flex items-center [&_[aria-label]>div]:size-5! [&_[aria-label]_img]:size-5! [&_[aria-label]_span]:text-xs!">
                  <AssignedUsersAvatarStack
                    userIds={details.assignedUserIds}
                    maxVisible={4}
                    nonInteractive
                  />
                </span>
              ) : (
                <Placeholder>Assign team members</Placeholder>
              )}
            </RowValueButton>
          </DropdownMenuTrigger>
        </AssigneesMenu>
      </Row>

      <TimeRow
        individualId={details.individualId}
        foreignNationalId={details.foreignNationalId}
        onOpen={onDone}
      />

      <div className="my-1 h-px bg-border" />

      <ImmigrationRows
        immigration={details.immigration}
        onPremiumChange={details.setPremiumProcessing}
      />

      <DeadlinesSection deadlines={details.immigration.deadlines} />

      <div className="mt-1 h-px bg-border" />

      <div className="flex items-center justify-center gap-1 pt-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 flex-1 justify-center text-xs"
          onClick={() => {
            navigate(caseSettingsPath(details));
            onDone?.();
          }}
        >
          <Settings2 className="size-3.5" /> Case Settings
        </Button>
        {clientId ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 flex-1 justify-center text-xs"
            onClick={() => navigate(clientProfilePath(clientId))}
          >
            <CircleUserRound className="size-3.5" /> Client Profile
          </Button>
        ) : null}
      </div>
    </div>
  );
};

/** Popover wrapper; `children` is the trigger element. */
export const CaseDetailsPopover: FC<{
  details: CaseDetails;
  children: ReactNode;
}> = ({ details, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-80 p-3">
        <CaseDetailsBody details={details} onDone={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
};
